/**
 * Refresh del token JWT in "single-flight": N richieste parallele che
 * ricevono 401 condividono UNA sola chiamata di refresh.
 *
 * Senza, una dashboard con molte chiamate simultanee scatena uno stampede
 * di refresh: il rate-limit del backend (auth_token 5/min) ne respinge
 * alcune con 429 e il vecchio interceptor trattava QUALSIASI errore come
 * "sessione scaduta", cancellando i token e buttando fuori l'utente.
 *
 * Regola: solo un rifiuto vero del refresh token (400/401 = invalido o
 * scaduto) chiude la sessione; 429, errori di rete o 5xx lasciano i token
 * al loro posto e la richiesta fallita riproverà al prossimo giro.
 */
export function makeTokenRefresher({ doRefresh, storage, onSessionExpired }) {
  let inflight = null
  // Una sola volta per refresher: altrimenti N richieste parallele senza
  // refresh token chiamano onSessionExpired() (e quindi la navigazione a
  // /login) una volta ciascuna, e la pagina non arriva mai a navigare perché
  // ogni nuova richiesta fallita la riassegna da capo — un ping-pong che
  // martella il server invece di una singola redirezione pulita.
  let sessionExpiredNotified = false

  return function refreshAccessToken() {
    if (!inflight) {
      const refreshToken = storage.getItem('refresh_token')
      if (!refreshToken) {
        // senza refresh token l'access token (se è rimasto, magari scaduto)
        // non potrà mai essere rinnovato: ogni richiesta futura si
        // arenerebbe in un 401 silenzioso per sempre, senza che l'utente
        // capisca perché (visto che chi chiama spesso si limita a un
        // fallback vuoto). Stesso trattamento del refresh token invalido:
        // si chiude la sessione e si rimanda al login.
        if (!sessionExpiredNotified) {
          sessionExpiredNotified = true
          storage.removeItem('access_token')
          storage.removeItem('refresh_token')
          onSessionExpired()
        }
        return Promise.reject(new Error('nessun refresh token'))
      }
      inflight = doRefresh(refreshToken)
        .then((access) => {
          storage.setItem('access_token', access)
          return access
        })
        .catch((err) => {
          const status = err.response?.status
          if ((status === 400 || status === 401) && !sessionExpiredNotified) {
            sessionExpiredNotified = true
            storage.removeItem('access_token')
            storage.removeItem('refresh_token')
            onSessionExpired()
          }
          throw err
        })
        .finally(() => {
          inflight = null
        })
    }
    return inflight
  }
}
